import type { Audit, Exploit, Faq, ProtocolMetadata } from "@/types";
import { SupListRow, SupListTable, SupText } from "@repo/core-ui";
import moment from "moment/moment";

import React, { useState, useMemo, Fragment } from "react";
import DetailsSection from "@/components/Protocol/About/Table/DetailsSection";
import ExploitsSection from "@/components/Protocol/About/Table/ExploitsSection";
import FaqSection from "@/components/Protocol/About/Table/FaqSection";

import { classNames } from "@/utils";
import SupUiMultiTag from "@/components/Ui/MultiTag";

interface TableProps {
  metadata: ProtocolMetadata;
}

type ProtocolTab = "details" | "audits" | "exploits" | "faq";
const ProtocolTabs = ["details", "audits", "exploits", "faq"] as ProtocolTab[];

export default function Table({ metadata }: TableProps) {
  const realProtocolTabs = useMemo(() => {
    return ProtocolTabs.filter(tab => {
      switch (tab) {
        default:
          return true;
        case "audits":
          return metadata.audits && metadata.audits.length > 0;
        case "faq":
          return metadata.faq && metadata.faq.length > 0;
        case "exploits":
          return metadata.exploits && metadata.exploits.length > 0;
      }
    });
  }, [metadata]);

  const convertDate = (time: string) => {
    return new Date(time).toLocaleDateString("en-US");
  };

  const [selectedTab, setSelectedTab] = useState<ProtocolTab>("details");

  return (
    <>
      <div className="flex shrink-0 items-center justify-center gap-[1px] divide-x divide-neutral-600 border-x-0.5 border-t-0.5 border-neutral-600 font-mono">
        {realProtocolTabs.map(tab => (
          <button
            key={`button_${tab}`}
            onClick={() => setSelectedTab(tab)}
            className={classNames(
              "basis-full py-2 text-xs uppercase leading-[12px] tracking-wider",
              selectedTab === tab ? "bg-neutral text-neutral-900" : "border-b-0.5 border-neutral-600 bg-[transparent]"
            )}
          >
            {tab.toUpperCase()}
          </button>
        ))}
      </div>

      {selectedTab === "details" && (
        <SupListTable className="flex-1 border-t-0 bg-neutral">
          {Array.isArray(metadata.chain_ids) && metadata.chain_ids.length > 0 ? (
            <SupListRow label="Chains" value={<SupUiMultiTag chains={metadata.chain_ids} showNames />} />
          ) : (
            <Fragment />
          )}
          {metadata.type ? <SupListRow label="Protocol type" value={<SupText variant="subtitle1">{metadata.type.replace("_", " ")}</SupText>} /> : <Fragment />}
          {metadata.deploy_date.toString() !== "0001-01-01T00:00:00Z" ? (
            <SupListRow label="Age" value={<SupText variant="subtitle1">{moment(metadata.deploy_date).fromNow()}</SupText>} />
          ) : (
            <Fragment />
          )}
          <SupListRow label="Last updated" value={<SupText variant="subtitle1">{convertDate(metadata.updated_at)}</SupText>} />
        </SupListTable>
      )}

      {selectedTab === "audits" && (
        <div className="scrollbar-table min-w-0 flex-1 overflow-y-auto border-0.5 border-t-0 border-neutral-600 bg-neutral">
          {Array.isArray(metadata.audits) &&
            metadata.audits.map((audit: Audit, index: number) => (
              <DetailsSection title={convertDate(audit.audit_date)} content={audit.audit_name} link={audit.audit_link} linkLabel={"View the audit"} key={`audits_${index}`} />
            ))}
        </div>
      )}

      {selectedTab === "exploits" && (
        <div className="scrollbar-table min-w-0 flex-1 overflow-y-auto border-0.5 border-t-0 border-neutral-600 bg-neutral">
          {Array.isArray(metadata.exploits) &&
            metadata.exploits.map((exploit: Exploit, index: number) => (
              <ExploitsSection title={convertDate(exploit.date)} content={exploit.amount} link={exploit.url} key={`exploits_${index}`} />
            ))}
        </div>
      )}

      {selectedTab === "faq" && (
        <div className="scrollbar-table min-w-0 flex-1 overflow-y-auto border-0.5 border-t-0 border-neutral-600 bg-neutral">
          {Array.isArray(metadata.faq) && metadata.faq.map((faq: Faq, index: number) => <FaqSection title={faq.question} content={faq.answer} key={`faq_${index}`} />)}
        </div>
      )}
    </>
  );
}
